import ConfirmModal from '@cashew/confirm-modal';
import Pagination from '@cashew/pagination';
import type { order } from '@cashew/types';
import cn from 'classnames';
import moment from 'moment';
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Input,
  List,
  PageWithNavigation,
  Tabs,
  Title,
} from '../../components';
import SettlementItem from '../../components/SettlementItem';
import './index.css';
import Excel from '../../assets/Excel';
import SettlementItemPartner from '../../components/SettlementItemPartner';
import { setSelectedMerchants } from '../../reducers/general';
import OrdersFilter, { getOrdersApi } from './OrdersFilter';

const { api } = require('@cashew/utils');

const ns = 'settlements';

const Container = ({
  key,
  list,
  records,
  pageSize,
  pageNumber,
  setPageNumber,
  setPageSize,
  t,
  isLoading,
  tabIndex,
}: any) => (
  <div key={key}>
    <div className={`${ns}__headers`}>
      <div className="flex flex-grow-1 justify-content-center items-center">
        <p />
        <p className="flex flex-column justify-content-center align-items-start w-[17%]">
          {t('settlements.table.column.id')}
        </p>
        <p className="flex flex-column w-[17%]">
          <div>
            {t('settlements.table.column.startDate')}-
            {t('settlements.table.column.endDate')}
          </div>
          {tabIndex !== 1 && (
            <div>({t('settlements.table.column.processedDate')})</div>
          )}
        </p>
        <p className="flex flex-column w-[17%]">
          <div>{t('settlements.table.column.partnerId')}</div>
          <div>{t('settlements.table.column.partnerName')}</div>
        </p>
        <p className="flex flex-column w-[17%]">
          <div>{t('settlements.table.column.amount')}</div>
          <div>{t('settlements.table.column.status')}</div>
        </p>
      </div>
    </div>
    <List data={list} component={SettlementItemPartner} isLoading={isLoading} />
    <Pagination
      records={records}
      pageSize={pageSize}
      pageNumber={pageNumber}
      setPageNumber={setPageNumber}
      changePageSize={setPageSize}
    />
  </div>
);

/**
 * Settlements Page RefundPage
 */
export default () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [list, setList] = useState<Array<order.OrderType> | undefined>(
    undefined
  );
  const [tabIndex, setTabIndex] = useState(0);
  const [records, setRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [startDate, setStartDate] = useState(
    moment().subtract(2, 'years').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [isProcessingSummaryReport, setIsProcessingSummaryReport] =
    useState(false);
  const [isProcessingDetailsReport, setIsProcessingDetailsReport] =
    useState(false);
  const [dateToError, setDateToError] = useState('');
  const [dateFromError, setDateFromError] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('PENDING,PROCESSED');
  const [params, setParams] = useState({});
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { selectedMerchants } = useSelector((state: any) => state.general);
  const { token } = useSelector((state: any) => state.user);
  const [searchId, setSearchId] = useState('');
  const [searchBy, setSearchBy] = useState<string>('merchantName');

  const reFetchOrders = async () => {
    await getOrdersApi(true, {
      setLoading: setIsLoading,
      setRecords,
      setList,
      pageSize,
      pageNumber,
      dispatch,
      setPageNumber,
      type: 'cashew',
      records,
      startDate,
      endDate,
      tabIndex,
      status,
      setStatus,
      setParams,
      token,
      searchId,
      searchBy,
      t,
    });
  };

  const handleMarkAsProcessed = async () => {
    try {
      await api.patch({
        endpoint: 'settlements/batch/status',
        params: selectedMerchants,
      });
      dispatch({
        type: 'OPEN_TOASTER',
        payload: {
          toasterTitle: 'Success',
          toasterMessage: 'Selected settlements have been marked as processed.',
          toasterType: 'success',
          toasterVisible: true,
          toasterDismiss: null,
        },
      });
      setPageNumber(pageNumber);
      dispatch(setSelectedMerchants([]));
      await reFetchOrders();
    } catch (error) {
      console.error('Failed to mark settlements as processed:', error);
      dispatch({
        type: 'OPEN_TOASTER',
        payload: {
          toasterTitle: 'Error',
          toasterMessage:
            'Failed to mark settlements as processed. Please try again.',
          toasterType: 'error',
          toasterVisible: true,
          toasterDismiss: null,
        },
      });
      dispatch(setSelectedMerchants([]));
      await reFetchOrders();
    } finally {
      setShowConfirmModal(false);
    }
  };

  useEffect(() => {
    history.push('partner-settlements');
  }, []);

  return (
    <div className={ns}>
      <Helmet>
        <title>{t('settlements.partner.sectionTitle')}</title>
      </Helmet>
      <PageWithNavigation>
        <Title>
          <span>{t('settlements.partner.title')}</span>
          <span>
            <Button
              extraClass="blue show-filter-btn"
              onClick={() => setShowFilter(!showFilter)}
            >
              <div className="btn-with-icon">
                <img src="/fillter.svg" alt="filter" />
                Filters
              </div>
            </Button>
          </span>
        </Title>
        <div className={cn({ 'hide-filter': !showFilter })}>
          <div className="filters-bar">
            <div className="filters">
              <div className="filter-group gap-2 gap-md-1 w-full flex min-h-[40px] flex-row flex-wrap justify-content-start align-items-end">
                <OrdersFilter
                  setLoading={setIsLoading}
                  page="orders"
                  type="cashew"
                  setRecords={setRecords}
                  setList={setList}
                  pageSize={pageSize}
                  pageNumber={pageNumber}
                  history={history}
                  dispatch={dispatch}
                  setPageNumber={setPageNumber}
                  records={records}
                  startDate={startDate}
                  endDate={endDate}
                  tabIndex={tabIndex}
                  status={status}
                  setStatus={setStatus}
                  setParams={setParams}
                />
                <div className="filter-input w-full lg:w-auto">
                  <label>From</label>
                  <Input
                    name="startDate"
                    value={startDate}
                    type="date"
                    error={dateFromError ? ' ' : ''}
                    onChange={(e: any) => {
                      setDateToError('');
                      setStartDate(e.currentTarget.value);

                      if (
                        moment(e.currentTarget.value).isAfter(endDate) &&
                        endDate
                      ) {
                        setDateFromError(t('validation-less'));
                      } else {
                        setDateFromError('');
                      }
                    }}
                  />
                  <span className="error-message">{dateFromError}</span>
                </div>
                <div className="filter-input w-full lg:w-auto">
                  <span>To</span>
                  <Input
                    name="endDate"
                    value={endDate}
                    type="date"
                    error={dateToError ? ' ' : ''}
                    onChange={(e: any) => {
                      setDateFromError('');
                      setEndDate(e.currentTarget.value);
                      if (
                        moment(e.currentTarget.value).isBefore(startDate) &&
                        startDate
                      ) {
                        setDateToError(t('validation-greater'));
                      } else {
                        setDateToError('');
                      }
                    }}
                  />
                  <span className="error-message">{dateToError}</span>
                </div>
                <div className='row gap-2 justify-content-start flex-row mt-2'>
                  <button
                    type="button"
                    className="rounded-2 col-auto flex min-h-[40px] flex justify-content-center items-center px-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    onClick={() => setShowConfirmModal(true)}
                    disabled={
                      !selectedMerchants || selectedMerchants.length === 0
                    }
                  >
                    Mark selection as processed
                  </button>
                  

                  <button
                      type="button"
                      className="rounded-2 col-auto flex justify-content-end items-center min-h-[40px] flex justify-content-center item-center px-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      disabled={isProcessingSummaryReport}
                      onClick={async (e) => {
                        setShowExportDropdown(false);
                        setIsProcessingSummaryReport(true);
                        await api
                          .get({
                            endpoint: 'settlements/excel-summary',
                            params,
                          })
                          .then((response: any) => {
                            const a = document.createElement('a');
                            a.href = response.data.downloadUrl;
                            document.body.appendChild(a);
                            a.click();
                          })
                          .catch((error: any) => {
                            console.log(error);
                            dispatch({
                              type: 'OPEN_TOASTER',
                              payload: {
                                toasterTitle: 'Error while generating report',
                                toasterMessage:
                                  'Something went wrong while generating report',
                                toasterType: 'error',
                                toasterVisible: true,
                                toasterDismiss: null,
                              },
                            });
                          });
                        setIsProcessingSummaryReport(false);
                      }}
                    >
                      {isProcessingSummaryReport
                        ? `${t('settlements.sectionFilter.exportSummary')}...`
                        : t('settlements.sectionFilter.exportSummary')}
                    </button>
      </div>
              
              </div>
            </div>
            
          </div>

         
       
        </div>
     
        <div className="space-div" />

        <Box withHeader>
          <Tabs
            headers={[
              t('settlements.tab.all'),
              t('settlements.tab.pending'),
              t('settlements.tab.processed'),
            ]}
            active={tabIndex}
            changeTab={(index: number) => {
              setTabIndex(index);
            }}
          >
            <Container
              key={0}
              list={list}
              records={records}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              ns={ns}
              t={t}
              isLoading={isLoading}
              tabIndex={tabIndex}
            />
            <Container
              key={1}
              list={list}
              records={records}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              ns={ns}
              t={t}
              isLoading={isLoading}
              tabIndex={tabIndex}
            />
            <Container
              key={2}
              list={list}
              records={records}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              ns={ns}
              t={t}
              isLoading={isLoading}
              tabIndex={tabIndex}
            />
          </Tabs>
        </Box>
      </PageWithNavigation>
      <ConfirmModal
        isVisible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        confirmTitle="Mark settlement"
        confirmDescription="Are you sure you want to mark as processed?"
        confirmButtons={
          <>
            <Button
              extraClass="secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              No
            </Button>
            <Button extraClass="blue" onClick={handleMarkAsProcessed}>
              Yes
            </Button>
          </>
        }
        showCloseButton
        resultTitle=""
        resultButtons={null}
        showResult={false}
        resultStatus="none"
      />
    </div>
  );
};
